import { FC, ReactElement, useCallback, useEffect } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { postInventeringUtkast } from 'store/bebyggelseobjektUtkastSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaDialog } from 'raa-react';

import { CenterActiveSourceGeometryButton } from 'components/CenterActiveSourceGeometryButton/CenterActiveSourceGeometryButton';
import ActionDropdown from 'components/IconDropdown/ActionDropdown';
import {
  isEnskiltBebyggelseobjekt,
  isOmrade,
  isSammansatt
} from 'components/bebyggelseobjekt/BebyggelseobjektPresentation';
import { DrawerSection } from 'components/drawerSections/DrawerSections';
import GeometryFileDownload from 'components/geometryFileDownload/GeometryFileDownload';
import ShareButton from 'components/share/ShareButton';
import SkeletonImage from 'components/skeleton/SkeletonImage';
import CategoryInfoDialog from 'components/utkast/categoryInfoDialog/CategoryInfoDialog';

import {
  Andamal,
  AndamalTag,
  BebyggelseobjektDomaner,
  BebyggelseobjektPublicerad,
  BebyggelseobjektUtkast,
  BebyggelseobjektVersionHistorisk,
  GeografiskIndelning,
  HTTP_STATUS,
  VinjettData
} from 'models';
import { RequestStatus } from 'models/appModels/Request';

import { AktivitetstypEnum, AuthorityRolesEnum, BebyggelseklassEnum, HandelseTypEnum, SkyddstypEnum } from 'enums';

import {
  ExtendedEnkelInventeringAktivitet,
  useDeleteAktivitetMutation,
  usePostAktivitetEnkelInventeringMutation,
  usePostAktivitetKulturvardeForstortMutation
} from 'api/endpoints/aktivitetPreliminarSlice';
import {
  getHandelseDateringDisplayNameForPresentation,
  getIconBySkydd,
  isSkyddHavt
} from 'utilities/bebyggelseobjektUtilities';
import { isBebyggelseobjektPublicerad, isBebyggelseobjektUtkast } from 'utilities/typeGuards';

interface BebyggelseTitleProps {
  data: BebyggelseobjektPublicerad | BebyggelseobjektUtkast | BebyggelseobjektVersionHistorisk;
  skyddstyp?: SkyddstypEnum;
  skyddstypNamn?: string;
  lm_andamal?: string;
  socken?: string;
  vinjett?: VinjettData;
  vinjettRequestStatus?: RequestStatus;
  domaner?: BebyggelseobjektDomaner;
}

const BebyggelseTitleSection: FC<BebyggelseTitleProps> = (props: BebyggelseTitleProps): ReactElement => {
  const navigate = useNavigate();
  const { setDialog, unsetDialog } = useDialog();
  const { rolesInSelectedOrganisation } = useAppSelector((state: RootState) => state.user);

  const getUtkastunderRubrik = (
    utkast: BebyggelseobjektUtkast | BebyggelseobjektPublicerad | BebyggelseobjektVersionHistorisk
  ): ReactElement => {
    if (isBebyggelseobjektUtkast(utkast)) {
      if (!utkast.lm_registerbyggnad && !utkast.lm_registerenhetsreferens) return <></>;

      const registerenhetsreferens = utkast?.lm_registerenhetsreferens?.map((f) => f.beteckning).join(', ') || '';
      const husnummer = utkast.lm_registerbyggnad?.husnummer || '';
      const underRubrik = `${registerenhetsreferens}${husnummer && `, hus ${husnummer}`}`;

      return <h2 className="tw-heading-md lg:tw-text-size-1.5">{underRubrik}</h2>;
    } else if (isBebyggelseobjektPublicerad(utkast)) {
      return <h2 className="tw-heading-md lg:tw-text-size-1.5">{utkast.berorda_fastigheter.join(', ')}</h2>;
    } else {
      return <></>;
    }
  };

  const getKommunAndLanText = (geografiskIndelning: GeografiskIndelning): string =>
    geografiskIndelning.kommun.map((kommun) => `${kommun.kommun_namn} kommun, ${kommun.lan_namn} län`).join(', ');

  const handleKategoriClick = useCallback(
    ({ andamal_id, andamal_namn }: Andamal) => {
      const huvudKategorier = props.domaner?.kategorityper.flatMap((h) => h.huvudkategorier) || [];
      const underkategorier = huvudKategorier.flatMap((h) => h.underkategorier) || [];
      const huvudKategori = huvudKategorier.find((a) => a.huvudkategorityp_id === andamal_id);
      const underKategori = underkategorier.find((a) => a.underkategorityp_id === andamal_id);

      const andamalTag: AndamalTag = {
        id: andamal_id,
        namn: andamal_namn,
        definitionstext: underKategori ? underKategori.definitionstext : huvudKategori?.definitionstext || '',
        kategorityp: underKategori ? 'UNDERKATEGORI' : 'HUVUDKATEGORI',
        nyckelord: underKategori ? underKategori.nyckelord : huvudKategori?.nyckelord || [],
        aktiv: true
      };

      setDialog(CategoryInfoDialog, { andamal: andamalTag, unsetDialog });
    },
    [props.domaner?.kategorityper, setDialog, unsetDialog]
  );

  return (
    <DrawerSection>
      {isBebyggelseobjektPublicerad(props.data) && (
        <div className="tw-flex tw-gap-2">
          <RedigeraListDropdown bebyggelseobjekt={props.data} roles={rolesInSelectedOrganisation} navigate={navigate} />
          <ShareButton />
        </div>
      )}
      <h1 className="tw-heading-lg tw-mb-2 lg:tw-text-size-2.5">{props.data.titel}</h1>
      {!isOmrade(props.data) && !isSammansatt(props.data) && getUtkastunderRubrik(props.data)}
      <div className="tw-mb-8 tw-mt-4 tw-flex tw-items-center tw-justify-between tw-text-center">
        {props.skyddstyp && (
          <div className="tw-flex tw-items-center">
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(
                getIconBySkydd(props.skyddstyp, isSkyddHavt(props.data))
              )}`}
              alt={`Ikon för skyddstyp ${props.skyddstypNamn}`}
            />
            <p className="tw-ml-2 tw-flex tw-text-center">
              {props.skyddstypNamn}
              {props.data.sammansatt?.havt_skydd && ' (HÄVT)'}
            </p>
          </div>
        )}
        {'geografisk_indelning' in props.data && (
          <p className="tw-flex tw-items-center">{getKommunAndLanText(props.data.geografisk_indelning)}</p>
        )}
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-start">
        <table className="tw-shrink tw-grow tw-basis-[50%]">
          <tbody className="tw-align-top [&>tr>th]:tw-pr-2 [&>tr>th]:tw-text-left">
            <tr>
              <th>Bebyggelsenummer</th>
              <td>{props.data.bebyggelsenummer}</td>
            </tr>
            {props.data.alternativnamn && !isOmrade(props.data) && props.data.alternativnamn.length > 0 && (
              <tr>
                <th>Alternativnamn</th>
                <td>{props.data.alternativnamn?.map((a) => (typeof a === 'string' ? a : a.namn)).join(', ')}</td>
              </tr>
            )}
            {props.data.ursprungligt_primar_andamal && (
              <tr>
                <th>{isOmrade(props.data) || isSammansatt(props.data) ? 'Områdeskategori' : 'Ursprungligt ändamål'}</th>
                <td>
                  <span>
                    <a
                      className="raa-link"
                      onClick={() => handleKategoriClick(props.data.ursprungligt_primar_andamal!)}
                    >
                      {props.data.ursprungligt_primar_andamal.andamal_namn}
                    </a>
                  </span>
                  {props.data.ursprungligt_sekundar_andamal
                    ?.slice()
                    .sort((a, b) => a.andamal_namn.localeCompare(b.andamal_namn))
                    .map((u, index) => [
                      ', ',
                      <span key={index}>
                        <a className="raa-link" onClick={() => handleKategoriClick(u)}>
                          {u.andamal_namn}
                        </a>
                      </span>
                    ])}
                </td>
              </tr>
            )}
            {props.lm_andamal && !isOmrade(props.data) && (
              <tr>
                <th className="tw-flex tw-max-w-[60%]">Lantmäteriets benämning</th>
                <td>{props.lm_andamal}</td>
              </tr>
            )}
            <tr>
              <th>Socken</th>
              <td>{props.socken}</td>
            </tr>
            {props.data.skydd?.forvaltningsorganisationsnamn && (
              <tr>
                <th>Förvaltare</th>
                <td>{props.data.skydd.forvaltningsorganisationsnamn}</td>
              </tr>
            )}
            {!isOmrade(props.data) && !isSammansatt(props.data) && (
              <tr>
                <th>Nybyggnad klar</th>
                <td>
                  {getHandelseDateringDisplayNameForPresentation(
                    props.data.handelser?.find((h) => h.handelsetyp === HandelseTypEnum.NYBYGGNAD)
                  )}
                </td>
              </tr>
            )}
            {(isOmrade(props.data) || isSammansatt(props.data)) && (
              <tr>
                <th>Tillkomst</th>
                <td>
                  {getHandelseDateringDisplayNameForPresentation(
                    props.data.handelser?.find((h) => h.handelsetyp === HandelseTypEnum.TILLKOMST)
                  )}
                </td>
              </tr>
            )}

            <tr>
              <th className="tw-align-top">Geometri</th>
              <td className="tw-flex tw-flex-wrap tw-gap-2">
                <CenterActiveSourceGeometryButton />
                {props.data.geometri && (
                  <GeometryFileDownload
                    fileName={props.data.bebyggelsenummer || 'Okänd'}
                    geometry={props.data.geometri}
                  ></GeometryFileDownload>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="tw-flex tw-shrink-0 tw-grow tw-basis-[175px] tw-flex-col tw-justify-start">
          {((props.vinjett?.url || props.vinjettRequestStatus === 'pending') && (
            <SkeletonImage
              className="tw-min-h-48 tw-w-full tw-object-cover"
              skeletonClassNames="tw-h-48 tw-w-full"
              src={props.vinjett?.url}
              alt="Vinjettbild"
            />
          )) || (
            <div className="tw-flex tw-h-48 tw-w-full tw-flex-col tw-items-center tw-justify-center tw-rounded-md tw-bg-raa-gray-3">
              <span className="raa-icon raa-icon-image tw-block"></span>
              Ingen bild
            </div>
          )}
        </div>
      </div>
    </DrawerSection>
  );
};

const RedigeraListDropdown: FC<{
  bebyggelseobjekt: BebyggelseobjektPublicerad;
  roles: AuthorityRolesEnum[];
  navigate: NavigateFunction;
}> = ({ bebyggelseobjekt, roles, navigate }): ReactElement => {
  const dispatch = useAppDispatch();
  const aktivitetDomaner = useAppSelector((state: RootState) => state.aktivitetDomaner.data);

  const [
    postEnkelInventering,
    {
      data: enkelinventeringAktivitet,
      isSuccess: postEnkelInventeringIsSuccess,
      isLoading: postEnkelInventeringIsLoading,
      reset: resetPostEnkelInventeringCache
    }
  ] = usePostAktivitetEnkelInventeringMutation();

  const [
    postKulturvardeForstort,
    {
      data: kulturvardeForstortAktivitet,
      isSuccess: postKulturvardeForstortAktivitetIsSuccess,
      isLoading: postKulturvardeForstortAktivitetIsLoading
    }
  ] = usePostAktivitetKulturvardeForstortMutation();

  const { selected_organisation: selectedOrg } = useAppSelector((state: RootState) => state.user);
  const [deleteAktivitet] = useDeleteAktivitetMutation();

  const { setDialog, unsetDialog } = useDialog();

  const redigeringSammansattSBMAllowed =
    isBebyggelseobjektPublicerad(bebyggelseobjekt) &&
    bebyggelseobjekt.bebyggelseklass === BebyggelseklassEnum.SAMMANSATT &&
    !isSkyddHavt(bebyggelseobjekt) &&
    bebyggelseobjekt.skydd.skyddstyp === SkyddstypEnum.STATLIGT_BYGGNADSMINNE &&
    roles.includes(AuthorityRolesEnum.SBM_HANDLAGGARE);

  const redigeringEnskiltBoOrOmradeAllowed =
    isBebyggelseobjektPublicerad(bebyggelseobjekt) &&
    (isOmrade(bebyggelseobjekt) || isEnskiltBebyggelseobjekt(bebyggelseobjekt)) &&
    roles.includes(AuthorityRolesEnum.BEBYGGELSEREGISTRERARE);

  const kulturvardeForstortAllowed =
    redigeringEnskiltBoOrOmradeAllowed && bebyggelseobjekt.skydd.skyddstyp === SkyddstypEnum.IDENTIFIERAT_KULTURVARDE;

  useEffect(() => {
    if (postKulturvardeForstortAktivitetIsSuccess && kulturvardeForstortAktivitet) {
      navigate(`/aktivitet/${kulturvardeForstortAktivitet.aktivitet_id}`);
    }
  }, [kulturvardeForstortAktivitet, postKulturvardeForstortAktivitetIsSuccess, navigate]);

  useEffect(() => {
    const postUtkast = async (aktivitet: ExtendedEnkelInventeringAktivitet) => {
      const response = await dispatch(
        postInventeringUtkast({
          bebyggelseobjektBefintligPost: {
            aktivitet_id: aktivitet.aktivitet_id,
            bebyggelseobjekt_identiteter: [bebyggelseobjekt.id]
          },
          ignoreHttpStatus: [HTTP_STATUS.UNPROCESSABLE_ENTITY]
        })
      );

      if ('error' in response) {
        deleteAktivitet({ eTag: aktivitet.eTag, aktivitetId: aktivitet.aktivitet_id });
        resetPostEnkelInventeringCache();

        setDialog(RaaDialog, {
          header: 'Det gick inte att skapa utkastet',
          ariaDescribedBy: 'dialog-description',
          children: (
            <p id="dialog-description" className="tw-mt-4">
              {response.payload?.status == HTTP_STATUS.UNPROCESSABLE_ENTITY
                ? 'Utkastet är låst till en annan aktivitet.'
                : response.payload?.message}
            </p>
          ),
          close: unsetDialog,
          cancel: unsetDialog,
          cancelButtonText: 'stäng'
        });

        return;
      }

      navigate(`/aktivitet/${aktivitet.aktivitet_id}`, { replace: true });
    };

    if (postEnkelInventeringIsSuccess && enkelinventeringAktivitet) {
      void postUtkast(enkelinventeringAktivitet);
    }
  }, [
    bebyggelseobjekt.id,
    deleteAktivitet,
    dispatch,
    enkelinventeringAktivitet,
    navigate,
    postEnkelInventeringIsSuccess,
    resetPostEnkelInventeringCache,
    setDialog,
    unsetDialog
  ]);

  if (redigeringSammansattSBMAllowed) {
    return (
      <ActionDropdown
        icon="raa-icon-edit"
        title="Redigera"
        items={[
          {
            text: 'Hävning av statligt byggnadsminne',
            onSelect: () => navigate(`/skyddsklassning/havning-av-sbm/${bebyggelseobjekt.id}`),
            isLoading: false
          },
          {
            text: 'Ägarbyte',
            onSelect: () => navigate(`/skyddsklassning/agarbyte/${bebyggelseobjekt.id}`),
            isLoading: false
          },
          {
            text: 'Revidering',
            onSelect: () => navigate(`/skyddsklassning/revidering/${bebyggelseobjekt.id}`),
            isLoading: false
          }
        ]}
      />
    );
  }

  if ((redigeringEnskiltBoOrOmradeAllowed || kulturvardeForstortAllowed) && selectedOrg) {
    const items = [];

    if (redigeringEnskiltBoOrOmradeAllowed) {
      items.push({
        text:
          aktivitetDomaner?.aktivitettyper.find((typ) => typ.aktivitettyp === AktivitetstypEnum.ENKELINVENTERING)
            ?.namn || 'Inventering av enskilt objekt',
        onSelect: async () => {
          postEnkelInventering({
            organisationsnummer: selectedOrg.organisation_nummer
          });
        },
        isLoading: postEnkelInventeringIsLoading
      });
    }

    if (kulturvardeForstortAllowed) {
      items.push({
        text:
          aktivitetDomaner?.aktivitettyper.find((typ) => typ.aktivitettyp === AktivitetstypEnum.KULTURVARDE_FORSTORT)
            ?.namn || 'Kulturvärde förstört',
        onSelect: async () => {
          postKulturvardeForstort({
            bebyggelseobjektId: bebyggelseobjekt.id,
            kulturvardeForstortPost: {
              organisationsnummer: selectedOrg.organisation_nummer
            }
          });
        },
        isLoading: postKulturvardeForstortAktivitetIsLoading
      });
    }

    return <ActionDropdown icon="raa-icon-edit" title="Redigera" items={items} />;
  }

  return <></>;
};

export default BebyggelseTitleSection;
